'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { useTheme, type ThemePreference } from './ThemeProvider';
import styles from './ThemeToggle.module.css';

interface ThemeToggleProps {
  /**
   * 'compact' (default) - 40×40 icon-only button. For navbar headers.
   * 'full'             - full-width row with icon + label. For mobile drawers.
   */
  variant?: 'compact' | 'full';
  className?: string;
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  variant = 'compact',
  className,
}) => {
  const { preference, toggle } = useTheme();
  const t = useTranslations('common.theme');

  const shortLabels: Record<ThemePreference, string> = {
    light: t('lightLabel'),
    dark: t('darkLabel'),
    system: t('systemLabel'),
  };
  const hintLabels: Record<ThemePreference, string> = {
    light: t('lightHint'),
    dark: t('darkHint'),
    system: t('systemHint'),
  };

  const buttonClass = [
    styles.button,
    variant === 'full' && styles.buttonFull,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      onClick={toggle}
      className={buttonClass}
      title={hintLabels[preference]}
      aria-label={hintLabels[preference]}
    >
      <ThemeIcon preference={preference} />
      {variant === 'full' ? (
        <span className={styles.label}>{shortLabels[preference]}</span>
      ) : (
        <span className={styles.srOnly}>{hintLabels[preference]}</span>
      )}
    </button>
  );
};

const ThemeIcon: React.FC<{ preference: ThemePreference }> = ({ preference }) => {
  if (preference === 'light') {
    return (
      <svg
        className={styles.icon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }
  if (preference === 'dark') {
    return (
      <svg
        className={styles.icon}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    );
  }
  // system
  return (
    <svg
      className={styles.icon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="14" rx="2" ry="2" />
      <path d="M8 21h8M12 18v3" />
    </svg>
  );
};
